import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import {Injectable, OnDestroy} from '@angular/core';
import {isUnauthorizedError} from '@aztrix/helpers';
import {
  combineLatest,
  interval,
  Observable,
  of,
  ReplaySubject,
  Subscription,
  throwError,
  timer,
} from 'rxjs';
import {catchError, first, mergeMap, startWith, switchMap, throttleTime} from 'rxjs/operators';

import {AuthService} from '../store/auth.service';

const RETRY_HEADER = 'X-Auth-Retried';

@Injectable()
export class AuthInterceptor implements HttpInterceptor, OnDestroy {
  private _subscription = new Subscription();
  private _checkAuthenticated = new ReplaySubject<void>(1);

  constructor(private authentication: AuthService) {
    this._subscription = combineLatest([
      this._checkAuthenticated,
      interval(20000).pipe(startWith(undefined)),
    ])
      .pipe(
        throttleTime(3000),
        mergeMap(() => this.authentication.checkAuthenticated()),
        catchError(() => of(undefined))
      )
      .subscribe();
  }

  ngOnDestroy() {
    this._subscription.unsubscribe();
  }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (req.params.has('_noAuth')) {
      req = req.clone({params: req.params.delete('_noAuth')});
      return next.handle(req);
    } else {
      return this.authentication.authToken$.pipe(
        first(),
        mergeMap((authToken) => {
          let alteredReq = req;
          if (authToken) {
            // eslint-disable-next-line @typescript-eslint/naming-convention
            alteredReq = req.clone({setHeaders: {AuthToken: authToken}});
          }
          return next.handle(alteredReq).pipe(
            catchError((err: HttpErrorResponse) => {
              if (isUnauthorizedError(err)) {
                if (!req.headers.has(RETRY_HEADER)) {
                  return timer(1000).pipe(
                    switchMap(() => this.authentication.checkAuthenticated()),
                    catchError(() => throwError(() => err)),
                    switchMap(() =>
                      this.authentication.authToken$.pipe(
                        first(),
                        switchMap((newToken) => {
                          if (!newToken) {
                            return throwError(() => err);
                          }
                          const retryReq = req.clone({
                            // eslint-disable-next-line @typescript-eslint/naming-convention
                            setHeaders: {AuthToken: newToken, [RETRY_HEADER]: '1'},
                          });
                          return next.handle(retryReq);
                        })
                      )
                    )
                  );
                }
                this._checkAuthenticated.next(undefined);
              }
              return throwError(() => err);
            })
          );
        })
      );
    }
  }
}
